/**
 * Manufacturer / technology brand catalogue.
 *
 * Rendered by `<BrandLogos />` on the homepage and on relevant service
 * pages. Each entry supports three modes:
 *
 *   1. `logoSrc` set  → the `<img>` renders the official asset from the
 *      public folder. Preferred once real press-kit files are placed at
 *      `/app/frontend/public/logos/<file>`.
 *   2. `logoSrc` null → the site renders a text wordmark in the display
 *      font, keeping the layout stable until an official file is added.
 *      No AI-generated or hand-traced logos exist in this repo.
 *   3. `href` optional → wraps the cell in an internal `<Link>` to the
 *      relevant Denti Service treatment page. External manufacturer
 *      sites are never linked (per requirement).
 *
 * The clinic explicitly asked NOT to imply partner / certification /
 * exclusivity — this file only lists product names actually used in
 * clinic. Nothing else.
 */

export const BRANDS = {
  megagen: {
    id: "megagen",
    name: "MegaGen",
    wordmarkStyle: "sans-strong",
    logoSrc: "/logos/megagen.svg",   // official SVG from megagen.co.uk theme assets
    logoW: 141, logoH: 35,
    href: "/implant-dentar-bacau",
  },
  swedenMartina: {
    id: "sweden-martina",
    name: "Sweden & Martina",
    wordmarkStyle: "serif",
    logoSrc: "/logos/sweden-martina.webp",   // optimized 400×89 WebP; original 2560×572 preserved as .png
    logoW: 400, logoH: 89,
    href: "/implant-dentar-bacau",
  },
  biolase: {
    id: "biolase",
    name: "BIOLASE",
    wordmarkStyle: "sans-strong",
    logoSrc: "/logos/biolase.png",
    logoW: 180, logoH: 61,
    href: "/parodontologie-laser",
  },
  waterlase: {
    id: "waterlase",
    name: "Waterlase",
    wordmarkStyle: "sans-strong",
    logoSrc: "/logos/waterlase.png",
    logoW: 789, logoH: 234,
    href: "/parodontologie-laser",
  },
  cerec: {
    id: "cerec",
    name: "CEREC",
    wordmarkStyle: "sans-strong",
    logoSrc: "/logos/cerec.png",
    logoW: 285, logoH: 59,
    href: "/coroane-dentare-cerec",
  },
  modjaw: {
    id: "modjaw",
    name: "MODJAW",
    wordmarkStyle: "sans-strong",
    logoSrc: "/logos/modjaw.svg",
    logoW: 85, logoH: 19,
    href: "/modjaw-4d-bacau",
  },
  sparkAligners: {
    id: "spark-aligners",
    name: "Spark Aligners",
    wordmarkStyle: "sans-strong",
    logoSrc: "/logos/spark-aligners.webp",  // optimized 300×120 WebP; original 1000×400 preserved as .png
    logoW: 300, logoH: 120,
    href: "/ortodontie",
  },
  drsmile: {
    id: "drsmile",
    name: "DrSmile",
    wordmarkStyle: "sans-strong",
    logoSrc: null,           // brand rebranded to "Impress" across all live markets (drsmile.de/.co.uk/.fr → Impress); drsmile.com sold to unrelated practice. No live official DrSmile mark served today.
    href: "/ortodontie",
  },
  emsDental: {
    id: "ems-dental",
    name: "EMS Dental",
    wordmarkStyle: "sans-strong",
    logoSrc: "/logos/ems-dental.webp",   // official EMS corporate wordmark from ems-dental.com/images/logo.webp (163x39)
    logoW: 163, logoH: 39,
    href: "/igienizare-dentara-ems",
  },
  dentsplySirona: {
    id: "dentsply-sirona",
    name: "Dentsply Sirona",
    wordmarkStyle: "sans-strong",
    logoSrc: "/logos/dentsply-sirona.svg",
    logoW: 100, logoH: 29,
    href: "/radiologie-digitala-cbct",
  },
};

// Homepage grid — exactly 10 brands, in the display order approved by the clinic.
export const HOMEPAGE_BRANDS = [
  BRANDS.megagen,
  BRANDS.swedenMartina,
  BRANDS.biolase,
  BRANDS.waterlase,
  BRANDS.cerec,
  BRANDS.modjaw,
  BRANDS.sparkAligners,
  BRANDS.emsDental,
  BRANDS.dentsplySirona,
];

// Contextual selections shown on individual treatment pages. Each entry
// may include a `supportingText` line rendered under the strip.
export const CONTEXTUAL_BRANDS = {
  implanturi: {
    brands: [BRANDS.megagen, BRANDS.swedenMartina],
    supportingText: null,
  },
  allOn4: {
    brands: [BRANDS.megagen, BRANDS.swedenMartina],
    supportingText: null,
  },
  cerec: {
    brands: [BRANDS.cerec, BRANDS.dentsplySirona, BRANDS.modjaw],
    supportingText:
      "Scanare Primescan · frezare Primemill · sinterizare SpeedFire · analiză funcțională MODJAW 4D",
  },
  protetica: {
    brands: [BRANDS.cerec, BRANDS.dentsplySirona, BRANDS.modjaw],
    supportingText:
      "Scanare Primescan · frezare Primemill · sinterizare SpeedFire · analiză funcțională MODJAW 4D",
  },
  parodontologie: {
    brands: [BRANDS.biolase, BRANDS.waterlase],
    supportingText: "Tehnologie Waterlase pentru tratamente minim invazive",
  },
  ortodontie: {
    brands: [BRANDS.sparkAligners, BRANDS.drsmile],
    supportingText: null,
  },
  igienizare: {
    brands: [BRANDS.emsDental],
    supportingText: "AIRFLOW · Guided Biofilm Therapy",
  },
  radiologie: {
    brands: [BRANDS.dentsplySirona],
    supportingText: "Imagistică digitală AXEOS 2D/3D",
  },
};
